/**
 * Win-Back Algorithm (frontend)
 *
 * Pure scoring helpers + default config, shared by the Win-Back report
 * (Reports.jsx) and the automated Win-Back config in Reward Manager
 * (Rewards.jsx). Keep DEFAULT_WINBACK_CONFIG in sync with the backend copy at
 * backend/src/config/winbackDefaults.js.
 */

export const DEFAULT_WINBACK_CONFIG = {
  // Value score weights (normalized by their sum)
  hoursWeight: 40,
  visitsWeight: 20,
  sessionsWeight: 5,
  avgWagerWeight: 25,
  visitGapWeight: 10,
  // Normalization scales (value at which a factor reaches full contribution)
  maxHours: 300,
  maxVisits: 30,
  maxSessions: 40,
  maxAvgWager: 200,
  visitGapReference: 21,
  // Recency multipliers (by days since last play)
  recencyBoost15to45: 1.3,
  recencyBoost46to90: 1.0,
  recencyBoost91to180: 0.7,
  recencyBoost180plus: 0.4,
  // Hybrid comp: tiers set the band, avg wager adjusts within it
  compTiers: [
    { minScore: 80, amount: 150, label: '$150' },
    { minScore: 65, amount: 100, label: '$100' },
    { minScore: 50, amount: 75, label: '$75' },
    { minScore: 35, amount: 50, label: '$50' },
    { minScore: 20, amount: 25, label: '$25' },
  ],
  wagerAdjustReference: 75,
  wagerAdjustMaxPct: 0.25,
  compRounding: 5,
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const clamp01 = (v) => clamp(v, 0, 1);

/**
 * Compute the 0-100 value score for a lapsed player.
 * Player fields (from bridge): TotalHours, TotalVisits, TotalSessions,
 * AvgWager, AvgDaysBetweenVisits (null for single-visit players).
 */
export function computeValueScore(player, config) {
  const c = { ...DEFAULT_WINBACK_CONFIG, ...(config || {}) };
  const hours = num(player.TotalHours);
  const visits = num(player.TotalVisits);
  const sessions = num(player.TotalSessions);
  const avgWager = num(player.AvgWager);
  const avgGapRaw = player.AvgDaysBetweenVisits;
  const avgGap = avgGapRaw == null ? null : num(avgGapRaw);

  const hoursNorm = clamp01(hours / Math.max(1, c.maxHours));
  const visitsNorm = clamp01(visits / Math.max(1, c.maxVisits));
  const sessionsNorm = clamp01(sessions / Math.max(1, c.maxSessions));
  const wagerNorm = clamp01(avgWager / Math.max(1, c.maxAvgWager));
  // Cadence: shorter average gap (more frequent regular) => higher urgency
  const cadenceNorm =
    avgGap == null || avgGap <= 0
      ? 0
      : clamp01(Math.max(1, c.visitGapReference) / avgGap);

  const totalWeight =
    num(c.hoursWeight) +
    num(c.visitsWeight) +
    num(c.sessionsWeight) +
    num(c.avgWagerWeight) +
    num(c.visitGapWeight);
  if (totalWeight <= 0) return 1;

  const raw =
    (hoursNorm * num(c.hoursWeight) +
      visitsNorm * num(c.visitsWeight) +
      sessionsNorm * num(c.sessionsWeight) +
      wagerNorm * num(c.avgWagerWeight) +
      cadenceNorm * num(c.visitGapWeight)) /
    totalWeight;

  return Math.max(1, Math.min(100, Math.round(raw * 100)));
}

/** Recency multiplier by days since last play. */
export function getRecencyMultiplier(daysSinceLastPlay, config) {
  const c = { ...DEFAULT_WINBACK_CONFIG, ...(config || {}) };
  const d = num(daysSinceLastPlay);
  if (d <= 45) return num(c.recencyBoost15to45);
  if (d <= 90) return num(c.recencyBoost46to90);
  if (d <= 180) return num(c.recencyBoost91to180);
  return num(c.recencyBoost180plus);
}

/** Final score = value score * recency multiplier (rounded to 0.1). */
export function computeFinalScore(valueScore, recencyMultiplier) {
  return Math.round(num(valueScore) * num(recencyMultiplier) * 10) / 10;
}

/**
 * Hybrid comp: pick the tier by final score, then nudge the amount by avg
 * wager relative to the reference, capped at +/- wagerAdjustMaxPct, rounded.
 * Returns { amount, baseAmount, label, swing } (amount 0 => no comp).
 */
export function computeComp(finalScore, avgWager, config) {
  const c = { ...DEFAULT_WINBACK_CONFIG, ...(config || {}) };
  const tiers = [...(c.compTiers || [])].sort((a, b) => b.minScore - a.minScore);
  const tier = tiers.find((t) => num(finalScore) >= num(t.minScore));
  if (!tier) return { amount: 0, baseAmount: 0, label: '', swing: 0 };

  const ref = num(c.wagerAdjustReference);
  const maxPct = num(c.wagerAdjustMaxPct);
  const w = num(avgWager);
  let swing = 0;
  if (w > 0 && ref > 0 && maxPct > 0) {
    swing = clamp(w / ref - 1, -maxPct, maxPct);
  }
  const rounding = Math.max(1, num(c.compRounding) || 5);
  let amount = num(tier.amount) * (1 + swing);
  amount = Math.max(0, Math.round(amount / rounding) * rounding);
  return { amount, baseAmount: num(tier.amount), label: `$${amount}`, swing };
}

/** Color classes for a comp amount (Tailwind, dark theme). */
export function getCompColor(amount) {
  if (amount >= 150) return { text: 'text-purple-400', bg: 'bg-purple-500/20' };
  if (amount >= 100) return { text: 'text-green-400', bg: 'bg-green-500/20' };
  if (amount >= 75) return { text: 'text-blue-400', bg: 'bg-blue-500/20' };
  if (amount >= 50) return { text: 'text-yellow-400', bg: 'bg-yellow-500/20' };
  if (amount >= 25) return { text: 'text-orange-400', bg: 'bg-orange-500/20' };
  return { text: 'text-gray-500', bg: '' };
}
